/**
 * Facebook Messenger Platform - Message Edits Webhook Types
 * 
 * These types represent the webhook event structure for message_edits events.
 * Triggered when a user edits a previously sent message.
 * 
 * @see https://developers.facebook.com/docs/messenger-platform/reference/webhook-events/message-edits/
 */

import {
  BaseWebhookEvent,
  WebhookEventType,
  WebhookPayload,
  BaseProcessingContext,
  extractBaseContext,
} from './base-types';

// Note: MessageEditSender and MessageEditRecipient are now replaced by
// WebhookSender and WebhookRecipient from base-types.ts
// Keeping these as deprecated aliases for backward compatibility

import { WebhookSender as MessageEditSender } from './base-types';
import { WebhookRecipient as MessageEditRecipient } from './base-types';
export { MessageEditSender, MessageEditRecipient };

/**
 * Represents the edited message information
 */
export interface MessageEdit {
  /** 
   * Unique message identifier for the edited message 
   */
  mid: string;
  
  /** 
   * New message content after the edit.
   * Contains the updated text of the message.
   */
  text: string;
  
  /** 
   * Number of times the message has been edited.
   * Maximum value is 5 (client-side constraint).
   */
  num_edit: number;
}

/**
 * Main webhook event structure for message edits with discriminator
 * 
 * This event is triggered when a user edits a previously sent message.
 * The webhook provides the updated message content and edit count.
 * 
 * @example
 * ```json
 * {
 *   "type": "message_edit",
 *   "sender": {
 *     "id": "1234567890123456"
 *   },
 *   "recipient": {
 *     "id": "9876543210987654"
 *   },
 *   "timestamp": 1458668856463,
 *   "message_edit": {
 *     "mid": "mid.1458668856218:ed81099e15d3f4f233",
 *     "text": "This is the updated message content",
 *     "num_edit": 2
 *   }
 * }
 * ```
 */
export interface MessageEditWebhookEvent extends BaseWebhookEvent {
  /** Discriminator for type narrowing */
  type: WebhookEventType.MESSAGE_EDIT;
  
  /** Details about the edited message */
  message_edit: MessageEdit;
}

/**
 * Complete webhook payload structure that includes the message edit event
 * along with other webhook metadata
 */
export interface MessageEditWebhookPayload extends WebhookPayload<MessageEditWebhookEvent> {}

/**
 * Type guard to check if a webhook event is a message edit event
 * 
 * @param event - The webhook event to check
 * @returns True if the event contains a message_edit property
 * 
 * @example
 * ```typescript
 * if (isMessageEditEvent(event)) {
 *   // TypeScript now knows event has message_edit property
 *   console.log(`Message edited ${event.message_edit.num_edit} times`);
 * }
 * ```
 */
export function isMessageEditEvent(event: any): event is MessageEditWebhookEvent {
  return event && typeof event === 'object' && 'message_edit' in event;
}

/**
 * Utility type for extracting just the message edit data from a webhook event
 */
export type MessageEditData = MessageEdit;

/**
 * Utility type for common message edit properties that might be used in processing
 */
export interface MessageEditProcessingContext extends BaseProcessingContext {
  /** The edited message ID */
  messageId: string;
  
  /** Updated message content */
  updatedText: string;
  
  /** Number of edits made */
  editCount: number;
}

/**
 * Helper function to extract processing context from a message edit event
 * 
 * @param event - The message edit webhook event
 * @returns Simplified processing context
 * 
 * @example
 * ```typescript
 * const context = extractMessageEditContext(webhookEvent);
 * console.log(`User ${context.senderId} edited message to: "${context.updatedText}"`);
 * ```
 */
export function extractMessageEditContext(event: MessageEditWebhookEvent): MessageEditProcessingContext {
  const baseContext = extractBaseContext(event);
  
  return {
    ...baseContext,
    messageId: event.message_edit.mid,
    updatedText: event.message_edit.text,
    editCount: event.message_edit.num_edit,
  };
}

/**
 * Constants related to message editing limits and constraints
 */
export const MESSAGE_EDIT_CONSTANTS = {
  /** Maximum number of edits allowed per message */
  MAX_EDITS: 5,
  
  /** Webhook event type identifier */
  EVENT_TYPE: 'message_edit' as const,
} as const;

